/**
 * Claude Code integration.
 *
 * Spawns `claude -p <prompt> --output-format text` as a child process for each prompt.
 * The prompt is passed as a CLI argument (not stdin).
 *
 * Conversation continuity: first prompt uses --session-id to create a session,
 * subsequent prompts use --resume to continue it. The session ID is derived
 * from the agent's ext_id so it's stable across restarts.
 *
 * The ARBI SKILL.md is injected via --append-system-prompt so Claude knows
 * which CLI commands are available.
 */

import { spawn } from 'node:child_process'
import { createHash } from 'node:crypto'
import { readFileSync } from 'node:fs'
import { resolve, dirname } from 'node:path'
import { fileURLToPath } from 'node:url'
import type { Orchestrator } from '../orchestrator.js'

export interface ClaudeOrchestratorOptions {
  /** Path to the claude CLI binary (defaults to "claude") */
  binaryPath?: string
  /** ARBI session identifier — used to maintain Claude conversation continuity */
  sessionId: string
  /** Additional CLI flags (e.g. ["--model", "opus"]) */
  extraArgs?: string[]
  /** Timeout in ms per prompt (defaults to 300_000 = 5 min) */
  timeoutMs?: number
  /** Override SKILL.md content (defaults to loading from arbi-cli package) */
  skillPrompt?: string
}

/**
 * Derive a deterministic UUID v4-shaped string from an arbitrary session ID.
 * Claude --session-id expects a UUID format.
 */
function toUuid(input: string): string {
  const hash = createHash('sha256').update(input).digest('hex')
  // Format as UUID: 8-4-4-4-12
  return [
    hash.slice(0, 8),
    hash.slice(8, 12),
    '4' + hash.slice(13, 16), // version 4
    '8' + hash.slice(17, 20), // variant
    hash.slice(20, 32),
  ].join('-')
}

/**
 * Fallback: try to load SKILL.md from nearby paths.
 * The CLI should pass skillPrompt explicitly — this is just a safety net.
 */
function loadSkillPrompt(): string | undefined {
  const here = dirname(fileURLToPath(import.meta.url))
  const candidates = [
    resolve(here, '..', 'SKILL.md'),
    resolve(here, '..', '..', 'arbi-cli', 'SKILL.md'),
  ]
  for (const candidate of candidates) {
    try {
      return readFileSync(candidate, 'utf-8')
    } catch {
      // Try next candidate
    }
  }
  return undefined
}

export class ClaudeOrchestrator implements Orchestrator {
  readonly name = 'claude'

  private readonly binaryPath: string
  private readonly extraArgs: string[]
  private readonly timeoutMs: number
  private readonly claudeSessionId: string
  private readonly skillPrompt: string | undefined
  private sessionStarted = false

  constructor(options: ClaudeOrchestratorOptions) {
    this.binaryPath = options.binaryPath ?? 'claude'
    this.extraArgs = options.extraArgs ?? []
    this.timeoutMs = options.timeoutMs ?? 300_000
    this.claudeSessionId = toUuid(options.sessionId)
    this.skillPrompt = options.skillPrompt ?? loadSkillPrompt()
  }

  async prompt(prompt: string): Promise<string> {
    // Try --resume first (works if session exists from a previous run or earlier prompt).
    // If it fails with "not found", fall back to --session-id to create a new session.
    try {
      return await this.spawnClaude(prompt, true)
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      if (
        msg.includes('not found') ||
        msg.includes('No session found') ||
        msg.includes('No conversation found')
      ) {
        return await this.spawnClaude(prompt, false)
      }
      throw err
    }
  }

  private spawnClaude(prompt: string, resume: boolean): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const args = [
        '-p',
        prompt,
        '--output-format',
        'text',
        // Auto mode: non-interactive, Claude decides what's safe to run
        '--permission-mode',
        'auto',
        // Resume existing session or create new one
        ...(resume ? ['--resume', this.claudeSessionId] : ['--session-id', this.claudeSessionId]),
        // Inject ARBI skill so Claude knows the available commands
        ...(this.skillPrompt ? ['--append-system-prompt', this.skillPrompt] : []),
        ...this.extraArgs,
      ]
      const child = spawn(this.binaryPath, args, {
        stdio: ['ignore', 'pipe', 'pipe'],
        env: { ...process.env, CLAUDECODE: undefined },
      })

      let stdout = ''
      let stderr = ''

      child.stdout.on('data', (chunk: Buffer) => {
        stdout += chunk.toString()
      })

      child.stderr.on('data', (chunk: Buffer) => {
        stderr += chunk.toString()
      })

      const timer = setTimeout(() => {
        child.kill('SIGTERM')
        reject(new Error(`Claude prompt timed out after ${this.timeoutMs}ms`))
      }, this.timeoutMs)

      child.on('close', (code) => {
        clearTimeout(timer)
        if (code !== 0) {
          reject(new Error(`Claude exited with code ${code}${stderr ? `: ${stderr.trim()}` : ''}`))
          return
        }
        resolve(stdout.trim())
      })

      child.on('error', (err) => {
        clearTimeout(timer)
        reject(new Error(`Failed to spawn claude: ${err.message}`))
      })
    })
  }

  async close(): Promise<void> {
    // No persistent resources to clean up — each prompt spawns a fresh process
  }
}
